/* ==========================================================================
   SWITCH Customs — theme.js

   Deliberately small. Navigation, filtering, search and variant URLs are all
   handled by real Shopify routes and real links, so this file only enhances
   four things:

     1. the menu panel and cart drawer (both work without JS via :target /
        a plain /cart link)
     2. the variant picker (options are real ?variant= links underneath)
     3. add-to-cart with priced add-ons (the base product still adds via a
        normal form POST if this never runs)
     4. the quote builder (both categories render as plain forms without JS)

   Cart state is never mirrored in JavaScript. After every mutation the cart
   drawer's markup is re-rendered by Liquid through the Section Rendering API
   and swapped in wholesale. There is nothing here that can drift out of sync
   with what the customer will actually be charged.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.SwitchTheme || {};
  var routes = CFG.routes || {};
  var strings = CFG.strings || {};

  /* ---------------------------------------------------------------- utils */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }

  /**
   * Guard against double-binding.
   *
   * boot() runs again on every `shopify:section:load` (the theme editor fires
   * it whenever a merchant edits a section). Without this, a merchant who
   * nudges a setting three times would get three submit handlers on the product
   * form — and three copies of the item in the cart on the next click.
   */
  var BOUND = new WeakMap();
  function once(el, key) {
    if (!el) return false;
    var keys = BOUND.get(el);
    if (!keys) { keys = {}; BOUND.set(el, keys); }
    if (keys[key]) return false;
    keys[key] = true;
    return true;
  }

  /** Shopify's money formatter, trimmed to the formats a COP shop uses. */
  function formatMoney(cents, format) {
    var fmt = format || CFG.moneyFormat || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function num(n, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(n) || n == null) return '0';
      n = (n / 100.0).toFixed(precision);
      var parts = n.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    switch ((fmt.match(placeholder) || [])[1]) {
      case 'amount':                                   value = num(cents, 2); break;
      case 'amount_no_decimals':                       value = num(cents, 0); break;
      case 'amount_with_comma_separator':              value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':  value = num(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':         value = num(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator':              value = num(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':  value = num(cents, 0, ' ', ','); break;
      default:                                         value = num(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  /** Locale strings ship with literal __TOKEN__ placeholders for JS to fill. */
  function fill(str, token, value) {
    return String(str || '').replace(token, value);
  }

  /* ------------------------------------------------- focus trap for panels */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function keydown(e) {
      if (e.key === 'Escape') { onEscape(); return; }
      if (e.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', keydown);
    return function () { document.removeEventListener('keydown', keydown); };
  }

  var scrollLocks = 0;
  function lockScroll() { scrollLocks++; document.body.classList.add('drawer-open'); }
  function unlockScroll() { scrollLocks = Math.max(0, scrollLocks - 1); if (!scrollLocks) document.body.classList.remove('drawer-open'); }

  /* ================================================== 1. Announcement bar */

  function initAnnouncement() {
    $$('[data-announcement-rotator]').forEach(function (bar) {
      if (!once(bar, 'rotator')) return;
      var slides = $$('[data-announcement-slide]', bar);
      if (slides.length < 2) return;
      var i = 0;
      var interval = parseInt(bar.getAttribute('data-interval'), 10) || 6000;
      setInterval(function () {
        slides[i].hidden = true;
        i = (i + 1) % slides.length;
        slides[i].hidden = false;
      }, interval);
    });
  }

  /* ========================================================= 2. Menu panel */

  function initMenu() {
    var panel = $('[data-menu-panel]');
    if (!panel || !once(panel, 'menu')) return;
    var opener = $('[data-menu-open]');
    var release = null;
    var lastFocus = null;

    function open(e) {
      if (e) e.preventDefault();
      lastFocus = document.activeElement;
      panel.classList.add('is-open');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      lockScroll();
      release = trapFocus(panel, close);
      var first = $(FOCUSABLE, panel);
      if (first) first.focus();
    }

    function close(e) {
      if (e) e.preventDefault();
      if (!panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      unlockScroll();
      if (release) { release(); release = null; }
      if (lastFocus) lastFocus.focus();
      // Clear the :target hash so the panel can't reopen itself on back/forward
      if (location.hash === '#MenuPanel') {
        history.replaceState(null, '', location.pathname + location.search);
      }
    }

    on(opener, 'click', open);
    $$('[data-menu-close]').forEach(function (btn) { on(btn, 'click', close); });

    // Someone arriving on a #MenuPanel link gets the JS version, not just :target
    if (location.hash === '#MenuPanel') open();
  }

  /* ========================================================= 3. Cart drawer */

  var CartDrawer = {
    wrapperId: 'shopify-section-cart-drawer',

    el: function () { return $('[data-cart-drawer]'); },

    isEnabled: function () {
      // The drawer only exists if the section rendered; otherwise fall back to /cart
      return !!this.el();
    },

    open: function () {
      var el = this.el();
      if (!el) return;
      el.classList.add('is-open');
      lockScroll();
      this._release = trapFocus(el, CartDrawer.close.bind(CartDrawer));
      var first = $('.drawer ' + FOCUSABLE, el);
      if (first) first.focus();
    },

    close: function (e) {
      if (e && e.preventDefault) e.preventDefault();
      var el = this.el();
      if (!el) return;
      el.classList.remove('is-open');
      unlockScroll();
      if (this._release) { this._release(); this._release = null; }
    },

    isOpen: function () {
      var el = this.el();
      return !!(el && el.classList.contains('is-open'));
    },

    /**
     * Swap in freshly rendered Liquid for the drawer, and copy the header's
     * cart badge across. Nothing is patched by hand — the server decides what
     * the cart looks like.
     */
    render: function (sections) {
      if (!sections) return;

      if (sections['cart-drawer']) {
        var wrapper = document.getElementById(this.wrapperId);
        if (wrapper) {
          var wasOpen = this.isOpen();
          var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          var fresh = parsed.getElementById(this.wrapperId);
          wrapper.innerHTML = fresh ? fresh.innerHTML : sections['cart-drawer'];
          if (wasOpen) {
            var el = this.el();
            if (el) el.classList.add('is-open');
          }
          this.bind();
        }
      }

      if (sections.header) {
        var head = new DOMParser().parseFromString(sections.header, 'text/html');
        var freshCount = head.querySelector('[data-cart-count]');
        var liveCount = $('[data-cart-count]');
        if (freshCount && liveCount) {
          liveCount.textContent = freshCount.textContent;
          liveCount.hidden = freshCount.hidden;
        }
        var freshA11y = head.querySelector('[data-cart-count-a11y]');
        var liveA11y = $('[data-cart-count-a11y]');
        if (freshA11y && liveA11y) liveA11y.textContent = freshA11y.textContent;
      }
    },

    /** Re-attach listeners to the freshly rendered drawer markup. */
    bind: function () {
      var el = this.el();
      if (!el) return;
      $$('[data-cart-close]', el).forEach(function (btn) {
        if (!once(btn, 'cartclose')) return;
        on(btn, 'click', CartDrawer.close.bind(CartDrawer));
      });
      bindCartControls(el);
    },

    /** Pull fresh markup without changing the cart. */
    refresh: function () {
      return fetch(window.location.pathname + '?sections=cart-drawer,header', {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (sections) { CartDrawer.render(sections); })
        .catch(function () { /* leave the current markup alone */ });
    }
  };

  function initCartDrawer() {
    var trigger = $('[data-cart-open]');

    // With the drawer disabled in settings, or missing, the icon stays a link.
    if (trigger && CartDrawer.isEnabled() && once(trigger, 'cartopen')) {
      on(trigger, 'click', function (e) {
        e.preventDefault();
        CartDrawer.open();
      });
    }

    CartDrawer.bind();

    // The /cart page renders its own copy of the lines, outside the drawer.
    // Bind those separately or its quantity and remove controls do nothing.
    var cartPage = $('[data-cart-page-form]');
    if (cartPage) bindCartControls(cartPage);
  }

  /* ------------------------------------------------- cart mutation helpers */

  function cartRequest(url, body, isFormData) {
    var opts = {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
    };
    if (isFormData) {
      // No Content-Type — the browser sets the multipart boundary itself.
      // This is what lets a real file upload ride along on a line item property.
      opts.body = body;
    } else {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  /**
   * Change quantities. Add-on lines follow their parent: removing a keychain
   * removes its engraving and magnet in the same request, so the cart can never
   * be left holding a paid extra for a product that's gone.
   */
  function updateLines(updates) {
    return cartRequest(routes.cart_update_url, {
      updates: updates,
      sections: 'cart-drawer,header',
      sections_url: window.location.pathname
    });
  }

  function companionKeys(group, scope) {
    if (!group) return [];
    return $$('[data-addon-of="' + group + '"]', scope || document).map(function (el) {
      return el.getAttribute('data-key');
    });
  }

  function bindCartControls(scope) {
    var root = scope || document;

    $$('[data-cart-remove]', root).forEach(function (btn) {
      if (!once(btn, 'remove')) return;
      on(btn, 'click', function () {
        var updates = {};
        updates[btn.getAttribute('data-key')] = 0;
        companionKeys(btn.getAttribute('data-group'), root).forEach(function (k) { updates[k] = 0; });
        btn.disabled = true;
        updateLines(updates)
          .then(function (data) { CartDrawer.render(data.sections); refreshCartPage(); })
          .catch(function () { btn.disabled = false; showCartError(); });
      });
    });

    function changeQty(key, group, qty) {
      if (qty < 0) qty = 0;
      var updates = {};
      updates[key] = qty;
      // Keep add-ons in step: one engraving per keychain, not per order.
      companionKeys(group, root).forEach(function (k) { updates[k] = qty; });
      return updateLines(updates)
        .then(function (data) { CartDrawer.render(data.sections); refreshCartPage(); })
        .catch(showCartError);
    }

    $$('[data-qty-up]', root).forEach(function (btn) {
      if (!once(btn, 'qtyup')) return;
      on(btn, 'click', function () {
        var line = btn.closest('[data-cart-line]');
        var input = $('[data-qty-input]', line);
        changeQty(btn.getAttribute('data-key'), line.getAttribute('data-group'), parseInt(input.value, 10) + 1);
      });
    });

    $$('[data-qty-down]', root).forEach(function (btn) {
      if (!once(btn, 'qtydown')) return;
      on(btn, 'click', function () {
        var line = btn.closest('[data-cart-line]');
        var input = $('[data-qty-input]', line);
        changeQty(btn.getAttribute('data-key'), line.getAttribute('data-group'), parseInt(input.value, 10) - 1);
      });
    });

    $$('[data-qty-input]', root).forEach(function (input) {
      if (!once(input, 'qtyinput')) return;
      on(input, 'change', function () {
        var line = input.closest('[data-cart-line]');
        changeQty(input.getAttribute('data-key'), line.getAttribute('data-group'), parseInt(input.value, 10) || 0);
      });
    });

    // Delivery zone -> cart attribute. No money maths, just a note on the order.
    $$('[data-zone-select]', root).forEach(function (select) {
      if (!once(select, 'zone')) return;
      on(select, 'change', function () {
        var attrs = {};
        attrs[select.name.replace(/^attributes\[|\]$/g, '')] = select.value;
        cartRequest(routes.cart_update_url, {
          attributes: attrs,
          sections: 'cart-drawer,header',
          sections_url: window.location.pathname
        })
          .then(function (data) { CartDrawer.render(data.sections); })
          .catch(showCartError);
      });
    });

    $$('[data-zone-form]', root).forEach(function (form) {
      if (!once(form, 'zoneform')) return;
      on(form, 'submit', function (e) { e.preventDefault(); });
    });
  }

  function showCartError() {
    var el = $('[data-form-error]');
    if (el) {
      el.textContent = strings.cartError || '';
      el.hidden = false;
    } else if (strings.cartError) {
      window.alert(strings.cartError);
    }
  }

  /** The /cart page renders its own copy of the lines, so reload it after edits. */
  function refreshCartPage() {
    if (!document.body.classList.contains('template-cart')) return;
    window.location.reload();
  }

  /* ====================================================== 4. Product page */

  function initProduct() {
    var root = $('[data-product-root]');
    if (!root || !once(root, 'product')) return;

    var form = $('[data-product-form]', root);
    var variants = [];
    var dataEl = $('[data-variant-data]', root);
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }
    }

    var addButton = form ? $('[data-add-button]', form) : null;
    var addLabel = addButton ? $('[data-add-label]', addButton) : null;
    var variantInput = form ? $('[data-variant-input]', form) : null;
    var groupField = form ? $('[data-group-field]', form) : null;
    var customNote = form ? $('[data-custom-note]', form) : null;
    var errorBox = form ? $('[data-form-error]', form) : null;

    function currentVariant() {
      if (!variantInput) return variants[0];
      var id = parseInt(variantInput.value, 10);
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) return variants[i];
      }
      return variants[0];
    }

    function activeAddons() {
      if (!form) return [];
      return $$('[data-addon]', form).filter(function (a) {
        return !a.hidden && a.getAttribute('data-active') === 'true';
      });
    }

    function updateTotal() {
      var v = currentVariant();
      if (!v) return;
      var total = v.price;
      activeAddons().forEach(function (a) {
        total += parseInt(a.getAttribute('data-addon-price'), 10) || 0;
      });

      if (addLabel) {
        addLabel.textContent = v.available
          ? (CFG.addLabelPattern || 'Agregar · __PRICE__').replace('__PRICE__', formatMoney(total))
          : (strings.soldOut || '');
      }
      if (addButton) addButton.disabled = !v.available;

      // The headline price tracks the add-ons too, so the number the customer
      // reads is the number they'll be charged for this line.
      var totalEl = $('[data-pdp-total]', root);
      if (totalEl) {
        totalEl.setAttribute('data-base-price', v.price);
        totalEl.textContent = formatMoney(total);
      }

      if (customNote) customNote.hidden = activeAddons().length === 0;
    }

    /* ------------------------------------------------------ variant picker */

    function selectVariant(id, pushUrl) {
      var v = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) { v = variants[i]; break; }
      }
      if (!v) return;

      if (variantInput) variantInput.value = v.id;

      // Highlight the chosen option in every option row
      $$('[data-variant-option]', root).forEach(function (a) {
        var matches = parseInt(a.getAttribute('data-variant-id'), 10) === v.id;
        if (matches) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });

      var compareEl = $('[data-pdp-compare]', root);
      if (compareEl) {
        if (v.compare_html) { compareEl.textContent = v.compare_html; compareEl.hidden = false; }
        else compareEl.hidden = true;
      }

      // Engraving is per variant. Hiding it isn't enough — an unchecked-but-
      // enabled input would still post an empty property, so switch it off too.
      var engrave = $('[data-addon="engrave"]', root);
      if (engrave) {
        var allowed = !!v.engravable;
        engrave.hidden = !allowed;
        if (!allowed && engrave.getAttribute('data-active') === 'true') setAddon(engrave, false);
      }

      if (v.featured_media_id) showMedia(v.featured_media_id);

      updateTotal();

      if (pushUrl && v.url) {
        history.replaceState({}, '', v.url);
      }
    }

    $$('[data-variant-option]', root).forEach(function (a) {
      on(a, 'click', function (e) {
        e.preventDefault();
        selectVariant(parseInt(a.getAttribute('data-variant-id'), 10), true);
      });
    });

    /* -------------------------------------------------------- media gallery */

    function showMedia(mediaId) {
      var main = $('[data-pdp-main-image]', root);
      var thumb = $('[data-pdp-thumb][data-media-id="' + mediaId + '"]', root);
      if (!main || !thumb) return;
      main.src = thumb.getAttribute('data-full');
      main.srcset = thumb.getAttribute('data-srcset') || '';
      main.alt = thumb.getAttribute('data-alt') || '';
      main.setAttribute('data-media-id', mediaId);
      $$('[data-pdp-thumb]', root).forEach(function (t) {
        if (t === thumb) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    $$('[data-pdp-thumb]', root).forEach(function (t) {
      on(t, 'click', function (e) {
        e.preventDefault();
        showMedia(t.getAttribute('data-media-id'));
      });
    });

    /* ---------------------------------------------------------- add-ons */

    function setAddon(addon, active) {
      addon.setAttribute('data-active', active ? 'true' : 'false');
      var head = $('[data-addon-toggle]', addon);
      var panel = $('.addon__panel', addon);
      var box = $('.addon__box', addon);

      if (head) head.setAttribute('aria-expanded', active ? 'true' : 'false');
      if (panel) panel.hidden = !active;
      if (box) {
        box.style.background = active ? 'var(--amber)' : 'transparent';
        box.style.borderColor = active ? 'var(--amber)' : 'var(--line-5)';
      }

      // Disabled inputs are not submitted. That's how an untouched add-on
      // avoids writing an empty line item property onto the order.
      $$('input, textarea, select', addon).forEach(function (input) {
        input.disabled = !active;
      });
      if (!active) {
        $$('input[type="file"]', addon).forEach(function (f) { f.value = ''; });
        var label = $('[data-dropzone-label]', addon);
        if (label && label.getAttribute('data-default')) label.textContent = label.getAttribute('data-default');
      }

      updateTotal();
    }

    $$('[data-addon]', root).forEach(function (addon) {
      addon.setAttribute('data-active', 'false');
      $$('input, textarea, select', addon).forEach(function (i) { i.disabled = true; });

      var head = $('[data-addon-toggle]', addon);
      on(head, 'click', function () {
        setAddon(addon, addon.getAttribute('data-active') !== 'true');
        if (addon.getAttribute('data-active') === 'true') {
          var firstField = $('input:not([type="file"]), textarea', addon);
          if (firstField) firstField.focus();
        }
      });

      // Engraving character counter
      var engraveInput = $('[data-engrave-input]', addon);
      var engraveCount = $('[data-engrave-count]', addon);
      if (engraveInput && engraveCount) {
        var max = engraveInput.getAttribute('data-max');
        var pattern = engraveCount.textContent.trim();
        on(engraveInput, 'input', function () {
          engraveInput.value = engraveInput.value.toUpperCase();
          engraveCount.textContent = pattern
            .replace(/^\d+/, String(engraveInput.value.length));
        });
      }

      // Photo dropzone
      var dropzone = $('[data-dropzone]', addon);
      var fileInput = $('[data-magnet-file]', addon);
      var label = $('[data-dropzone-label]', addon);
      if (dropzone && fileInput && label) {
        label.setAttribute('data-default', label.textContent);

        function acceptFile(file) {
          if (!file) return;
          if (!/^image\//.test(file.type)) {
            label.textContent = strings.fileWrongType || '';
            fileInput.value = '';
            return;
          }
          if (file.size > (CFG.maxUploadBytes || 20971520)) {
            label.textContent = strings.fileTooBig || '';
            fileInput.value = '';
            return;
          }
          label.textContent = fill(strings.fileReady, '__NAME__', file.name);
        }

        on(fileInput, 'change', function () { acceptFile(fileInput.files[0]); });

        ['dragenter', 'dragover'].forEach(function (ev) {
          on(dropzone, ev, function (e) { e.preventDefault(); dropzone.classList.add('is-over'); });
        });
        ['dragleave', 'dragend'].forEach(function (ev) {
          on(dropzone, ev, function () { dropzone.classList.remove('is-over'); });
        });
        on(dropzone, 'drop', function (e) {
          e.preventDefault();
          dropzone.classList.remove('is-over');
          var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (!file) return;
          // Put the dropped file into the real input so it posts with the form
          var dt = new DataTransfer();
          dt.items.add(file);
          fileInput.files = dt.files;
          acceptFile(file);
        });
      }
    });

    /* ------------------------------------------------------- add to cart */

    if (form) {
      on(form, 'submit', function (e) {
        e.preventDefault();
        if (errorBox) errorBox.hidden = true;

        var addons = activeAddons();
        var group = null;

        if (addons.length) {
          group = 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
          if (groupField) { groupField.disabled = false; groupField.value = group; }
        } else if (groupField) {
          groupField.disabled = true;
        }

        if (addButton) addButton.disabled = true;

        var fd = new FormData(form);
        if (!addons.length) {
          fd.append('sections', 'cart-drawer,header');
          fd.append('sections_url', window.location.pathname);
        }

        // Step 1: the product itself, carrying the engraved text and the photo
        // as line item properties. Multipart, so the file really uploads.
        cartRequest(routes.cart_add_url, fd, true)
          .then(function (data) {
            if (!addons.length) return data;

            // Step 2: the priced add-on lines, pointing back at the parent.
            var items = addons.map(function (a) {
              return {
                id: parseInt(a.getAttribute('data-addon-variant'), 10),
                quantity: 1,
                properties: {
                  _addon_for: group,
                  _para: form.getAttribute('data-product-title') || ''
                }
              };
            });
            return cartRequest(routes.cart_add_url, {
              items: items,
              sections: 'cart-drawer,header',
              sections_url: window.location.pathname
            }, false).catch(function () {
              // The product is in the cart but its extras are not. Say so
              // plainly rather than pretending the order is complete.
              throw { __addonFailure: true };
            });
          })
          .then(function (data) {
            CartDrawer.render(data.sections);
            if (CartDrawer.isEnabled()) CartDrawer.open();
            else window.location.href = routes.cart_url;

            // Reset the form's add-ons so a second add doesn't reuse the photo
            $$('[data-addon]', form).forEach(function (a) { setAddon(a, false); });
          })
          .catch(function (err) {
            if (errorBox) {
              errorBox.textContent = err && err.__addonFailure
                ? (strings.addonFailed || '')
                : ((err && (err.description || err.message)) || strings.cartError || '');
              errorBox.hidden = false;
            }
            if (err && err.__addonFailure) CartDrawer.refresh();
          })
          .then(function () {
            if (addButton) addButton.disabled = false;
            updateTotal();
          });
      });
    }

    // Establish the initial state from whatever the server rendered
    var startVariant = currentVariant();
    if (startVariant) {
      var engrave = $('[data-addon="engrave"]', root);
      if (engrave) engrave.hidden = !startVariant.engravable;
      updateTotal();
    }
  }

  /* ==================================================== 5. Accordions */

  function initAccordions() {
    $$('[data-acc-exclusive]').forEach(function (group) {
      if (!once(group, 'acc')) return;
      var items = $$('details', group);
      items.forEach(function (item) {
        on(item, 'toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) { if (other !== item) other.open = false; });
        });
      });
    });
  }

  /* ================================================== 6. Quote builder */

  function initQuote() {
    var panels = $$('[data-quote-panel]');
    if (!panels.length || !once(panels[0], 'quote')) return;

    var tabs = $$('[data-quote-tab]');

    function activate(slug) {
      var found = false;
      panels.forEach(function (p) {
        var match = p.getAttribute('data-quote-panel') === slug;
        p.hidden = !match;
        if (match) found = true;
      });
      if (!found && panels.length) {
        // Unknown ?tipo= — fall back to the first category rather than a blank page
        panels[0].hidden = false;
        slug = panels[0].getAttribute('data-quote-panel');
      }
      tabs.forEach(function (t) {
        if (t.getAttribute('data-quote-tab') === slug) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    tabs.forEach(function (t) {
      on(t, 'click', function (e) {
        e.preventDefault();
        var slug = t.getAttribute('data-quote-tab');
        activate(slug);
        history.replaceState({}, '', location.pathname + '?tipo=' + encodeURIComponent(slug));
      });
    });

    // Compose the WhatsApp message from whatever the customer has filled in
    panels.forEach(function (panel) {
      var wa = $('[data-quote-wa]', panel);
      var preview = $('[data-quote-preview]', panel);
      var fields = $$('[data-quote-field]', panel);
      var errorBox = $('[data-quote-error]', panel);

      function compose() {
        var title = wa ? wa.getAttribute('data-wa-title') : '';
        var lines = ['Cotización — ' + title, ''];
        fields.forEach(function (f) {
          var val = (f.value || '').trim();
          if (val) lines.push(f.getAttribute('data-label') + ': ' + val);
        });
        return lines.join('\n');
      }

      function sync() {
        var msg = compose();
        if (preview) preview.textContent = msg;
        if (wa) wa.href = wa.getAttribute('data-wa-base') + encodeURIComponent(msg);
      }

      fields.forEach(function (f) {
        on(f, 'input', sync);
        on(f, 'change', sync);
      });
      sync();

      // Don't let someone fire off a quote with nothing useful in it
      if (wa) {
        on(wa, 'click', function (e) {
          var missing = fields.filter(function (f) {
            return f.hasAttribute('required') && !(f.value || '').trim();
          });
          if (missing.length) {
            e.preventDefault();
            if (errorBox) {
              errorBox.textContent = strings.requiredMissing || 'Completa los campos obligatorios.';
              errorBox.hidden = false;
            }
            missing[0].focus();
            return;
          }
          if (errorBox) errorBox.hidden = true;
          sync();
        });
      }
    });

    var params = new URLSearchParams(location.search);
    activate(params.get('tipo') || panels[0].getAttribute('data-quote-panel'));
  }

  /* ============================================= 7. Auto-submitting selects */

  function initAutoSubmit() {
    $$('form.js-autosubmit select').forEach(function (select) {
      if (!once(select, 'autosubmit')) return;
      on(select, 'change', function () { select.form.submit(); });
    });
  }

  /* ======================================================== boot */

  function boot() {
    initAnnouncement();
    initMenu();
    initCartDrawer();
    initProduct();
    initAccordions();
    initQuote();
    initAutoSubmit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run bindings when the theme editor swaps a section in
  document.addEventListener('shopify:section:load', boot);
})();
